import React, { useState, useEffect } from 'react';
import { Table, Button, Space, Modal, Input, Select, Row, Col, message, Upload, Tooltip } from 'antd';
import { PlusOutlined, SearchOutlined, DeleteOutlined, EditOutlined, EyeOutlined, DownloadOutlined, UploadOutlined } from '@ant-design/icons';
import { studentAPI, schoolAPI, classAPI } from '../../services/api';
import api from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';

const { Option } = Select;
const { confirm } = Modal;

const normalizeId = (value) => (
  value === undefined || value === null || value === '' ? null : String(value)
);

const getUserSchoolId = (user) => normalizeId(
  user?.workbenchSchoolId ?? user?.schoolId ?? user?.school_id ?? user?.schoolID ?? user?.schoolIds?.[0] ?? user?.school_ids?.[0]
);

const resolveDefaultSchoolId = (user, availableSchools) => {
  const currentSchoolId = getUserSchoolId(user);
  if (availableSchools.some((school) => normalizeId(school.id) === currentSchoolId)) {
    return currentSchoolId;
  }

  // 旧默认学校确实已不在可访问范围时，才按用户实际班级归属回退。
  // “待绑定学校”同样是合法使用状态，不能按名称过滤或强制迁移。
  const classSchoolIds = (user?.classes || [])
    .map((item) => normalizeId(item?.schoolId ?? item?.school_id))
    .filter(Boolean);
  const classSchool = availableSchools.find((school) => classSchoolIds.includes(normalizeId(school.id)));
  if (classSchool) return normalizeId(classSchool.id);

  return availableSchools[0] ? normalizeId(availableSchools[0].id) : currentSchoolId;
};

const StudentList = () => {
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [schools, setSchools] = useState([]);
  const [classes, setClasses] = useState([]);
  const [selectedSchool, setSelectedSchool] = useState(null);
  const [selectedClass, setSelectedClass] = useState(null);
  const [searchName, setSearchName] = useState('');
  const [loadingImport, setLoadingImport] = useState(false);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]); // 新增：选中的行
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0
  });
  const { currentUser, isAuthenticated, loading: authLoading } = useAuth();
  const navigate = useNavigate();

  // 获取学校列表
  const fetchSchools = async () => {
    console.log('获取学校列表开始');
    if (!isAuthenticated || !currentUser) {
      console.log('获取学校列表取消：用户未登录');
      return;
    }
    
    try {
      console.log('请求学校列表数据');
      const response = await schoolAPI.getList();
      const rows = response?.data?.data || [];
      const schoolsData = currentUser.workbenchSchoolId ? rows.filter(s => String(s.id) === currentUser.workbenchSchoolId) : rows;
      console.log('获取到 ' + schoolsData.length + ' 所学校');
      setSchools(schoolsData);

      // 保留用户原来的有效学校作为默认值，但不锁死教师或多学校管理员。
      const defaultSchoolId = resolveDefaultSchoolId(currentUser, schoolsData);
      if (currentUser && (currentUser.workbenchSchoolId || ['school_admin', 'teacher'].includes(currentUser.role)) && defaultSchoolId) {
        console.log('当前用户默认选择学校:', defaultSchoolId);
        setSelectedSchool(defaultSchoolId);
        fetchClasses(defaultSchoolId);
      }
    } catch (error) {
      console.error('获取学校列表失败:', error);
      message.error('获取学校列表失败: ' + (error.response?.data?.message || error.message || '未知错误'));
    }
  };

  // 获取班级列表
  const fetchClasses = async (schoolId) => {
    if (!schoolId) {
      setClasses([]);
      return;
    }

    try {
      const response = await classAPI.getAll({ schoolId, pageSize: 200 });
      // 按学校在服务端筛选，避免一次性拉取全平台班级。
      setClasses(response.data.data || []);
    } catch (error) {
      console.error('获取班级列表失败:', error);
      message.error('获取班级列表失败');
    }
  };

  // 获取学生列表
  const fetchStudents = async (page = 1, pageSize = 10) => {
    console.log('获取学生列表开始');
    console.log('认证状态检查:', { isAuthenticated, currentUser, token: localStorage.getItem('token') });
    
    if (!isAuthenticated || !currentUser) {
      console.log('获取学生列表取消：用户未登录');
      message.error('请先登录');
      return;
    }

    // 优雅处理 React Click 对象的 nativeEvent，防止其被误解析为页码
    let targetPage = page;
    let targetPageSize = pageSize;
    if (page && typeof page === 'object' && page.nativeEvent) {
      targetPage = pagination.current;
      targetPageSize = pagination.pageSize;
    }

    setLoading(true);
    try {
      console.log('使用studentAPI获取学生数据');
      
      // 构建参数，支持分页
      const params = {
        page: targetPage,
        pageSize: targetPageSize
      };
      
      // 明确选择优先；仅在尚未选择时回退到用户原来的有效学校。
      const currentSchoolId = getUserSchoolId(currentUser);
      if (currentUser.workbenchSchoolId) {
        params.schoolId = currentUser.workbenchSchoolId;
      } else if (selectedSchool) {
        params.schoolId = selectedSchool;
      } else if (['school_admin', 'teacher'].includes(currentUser.role) && currentSchoolId) {
        console.log('当前用户回退到原学校ID:', currentSchoolId);
        params.schoolId = currentSchoolId;
      }
      
      // 添加其他过滤条件
      if (selectedClass) {
        params.classId = selectedClass;
      }
      
      if (searchName && searchName.trim()) {
        params.searchName = searchName.trim();
      }
      
      console.log('查询参数:', params);
      console.log('请求URL:', 'http://127.0.0.1:4100/api/v2/students');
      
      // 使用studentAPI获取数据
      const response = await studentAPI.getAll(params);
      console.log('studentAPI.getAll响应:', response);
      
      if (response && response.data && response.data.success) {
        const studentsData = response.data.data || [];
        console.log(`成功获取 ${studentsData.length} 条学生记录`);
        setStudents(studentsData);
        
        // 更新分页参数
        setPagination({
          current: response.data.pagination?.current || targetPage,
          pageSize: response.data.pagination?.pageSize || targetPageSize,
          total: response.data.pagination?.total || 0
        });
        
        if (studentsData.length === 0) {
          message.info('暂无学生数据');
        }
      } else {
        console.error('获取学生列表失败:', response?.data?.message || '未知错误');
        message.error('获取学生列表失败: ' + (response?.data?.message || '未知错误'));
      }
    } catch (error) {
      console.error('获取学生列表异常:', error);
      message.error('获取学生列表异常: ' + (error.message || '未知错误'));
    } finally {
      setLoading(false);
    }
  };

  // 组件挂载时获取数据
  useEffect(() => {
    console.log('StudentList组件挂载');
    console.log('认证状态:', { isAuthenticated, authLoading, currentUser });
    
    // 仅在认证加载完成后执行
    if (!authLoading) {
      if (isAuthenticated && currentUser) {
        console.log('用户已认证，获取学校列表');
        fetchSchools();
      } else {
        console.log('用户未认证或无用户信息');
        message.warning('请先登录');
      }
    }
  }, [authLoading, isAuthenticated, currentUser]);

  // 监听学校列表加载完成
  useEffect(() => {
    // 如果学校列表不为空且用户已登录，则获取学生数据，默认第一页
    if (schools.length > 0 && isAuthenticated && currentUser) {
      console.log('学校列表加载完成，开始获取学生数据');
      fetchStudents(1, pagination.pageSize);
    }
  }, [schools, isAuthenticated, currentUser]);

  // 监听过滤条件变化，重置到第一页
  useEffect(() => {
    // 如果有用户登录且已经有学校数据，则根据过滤条件重新获取
    if (isAuthenticated && currentUser && schools.length > 0 && (selectedSchool || selectedClass || searchName)) {
      console.log('过滤条件变化，重新获取学生数据');
      fetchStudents(1, pagination.pageSize);
    }
  }, [selectedSchool, selectedClass, searchName, isAuthenticated, currentUser, schools.length]);

  // 学校变化时获取相应班级
  const handleSchoolChange = (value) => {
    setSelectedSchool(value);
    setSelectedClass(null);
    fetchClasses(value);
  };

  // 删除学生
  const handleDelete = (studentId) => {
    confirm({
      title: '永久删除学生及关联资料',
      content: '将同时永久删除该学生的档案、座位、积分、考勤、作业、附件、家长绑定及兼容数据，不保留可识别痕迹且无法恢复。确定继续吗？',
      okText: '永久删除',
      okType: 'danger',
      cancelText: '取消',
      onOk: async () => {
        try {
          await studentAPI.delete(studentId);
          message.success('学生删除成功');
          fetchStudents();
        } catch (error) {
          console.error('删除学生失败:', error);
          message.error('删除学生失败');
        }
      }
    });
  };

  // 批量删除学生
  const handleBatchDelete = () => {
    if (selectedRowKeys.length === 0) {
      message.warning('请先选择要删除的学生');
      return;
    }

    confirm({
      title: '批量永久删除学生及关联资料',
      content: `将永久删除选中的 ${selectedRowKeys.length} 个学生，并同步删除档案、座位、积分、考勤、作业、附件、家长绑定及兼容数据，不保留可识别痕迹且无法恢复。`,
      okText: '永久删除',
      okType: 'danger',
      cancelText: '取消',
      onOk: async () => {
        try {
          const response = await api.post('/students/batch-delete', {
            studentIds: selectedRowKeys,
            schoolId: selectedSchool || getUserSchoolId(currentUser)
          });
          
          if (response.data.success) {
            message.success(`批量删除成功！已删除 ${response.data.data.deletedCount} 个学生`);
            setSelectedRowKeys([]); // 清空选中项
            fetchStudents(); // 重新加载学生列表
          } else {
            message.error('批量删除失败: ' + response.data.message);
          }
        } catch (error) {
          console.error('批量删除失败:', error);
          message.error('批量删除失败: ' + (error.response?.data?.message || error.message));
        }
      },
    });
  };

  // 清空选择
  const handleClearSelection = () => {
    setSelectedRowKeys([]);
    message.info('已清空选择');
  };

  // 下载导入模板
  const handleDownloadTemplate = async () => {
    try {
      // 使用配置的API baseURL，类似于通讯录的实现
      window.location.href = `${api.defaults.baseURL}/students/import-template/noauth`;
      
      message.success('正在下载模板文件...');
    } catch (error) {
      console.error('下载模板出错:', error);
      message.error('下载模板失败，请稍后再试');
    }
  };

  // 处理文件上传
  const handleFileUpload = async (options) => {
    const { file, onSuccess, onError } = options;
    
    // 班级归属优先，其次使用用户明确选择的学校；不再强制覆盖为缓存中的单一学校。
    const selectedClassRecord = classes.find((item) => normalizeId(item.id) === normalizeId(selectedClass));
    const uploadSchoolId = normalizeId(selectedClassRecord?.school_id ?? selectedClassRecord?.schoolId)
      || normalizeId(selectedSchool)
      || getUserSchoolId(currentUser);
    const uploadClassId = selectedClass;
    
    if (!uploadSchoolId) {
      message.error('请先选择学校');
      onError('请先选择学校');
      return;
    }
    if (currentUser.role === 'teacher' && !uploadClassId) {
      message.error('教师导入学生资料前请先选择班级');
      onError('请先选择班级');
      return;
    }

    const formData = new FormData();
    formData.append('file', file);
    formData.append('schoolId', uploadSchoolId);
    if (uploadClassId) {
      formData.append('classId', uploadClassId);
    }

    setLoadingImport(true);
    message.loading('正在导入学生数据，请稍候...', 1);
    
    try {
      console.log('开始上传文件，学校ID:', uploadSchoolId, '班级ID:', uploadClassId, '文件名:', file.name, '用户角色:', currentUser.role);
      const response = await studentAPI.importStudents(formData);
      console.log('导入响应:', response);
      onSuccess(response, file);
      
      const data = response.data.data || {};
      const { success, failed, errors } = data;
      const restoredCount = Number(data.restoredCount || 0);
      const restoredRows = Array.isArray(data.restoredRows) ? data.restoredRows : [];
      
      if (restoredCount > 0) {
        const restoredNames = restoredRows
          .slice(0, 8)
          .map((item) => item.studentName)
          .filter(Boolean)
          .join('、');
        Modal.info({
          title: `导入完成：${restoredCount} 名旧版软删除资料已清理并重新创建`,
          content: (
            <div>
              <p>系统检测到并清理了旧版软删除残留，本次已按上传文件创建全新资料；系统不会保留重新识别标记。</p>
              {restoredNames ? <p>涉及学生：{restoredNames}{restoredRows.length > 8 ? '等' : ''}</p> : null}
              <p>原座位、积分、考勤、作业和家长绑定等历史数据不会恢复。</p>
              {failed > 0 ? <p>另有 {failed} 条导入失败，请核对导入错误。</p> : null}
            </div>
          )
        });
      } else if (failed > 0 && errors && errors.length > 0) {
        // 如果有导入失败的记录，显示更详细的信息
        const errorMessages = errors.slice(0, 3).map(err => 
          `行 ${err.record ? Object.values(err.record)[0] : '未知'}: ${err.error}`
        ).join('\n');
        
        const moreErrors = errors.length > 3 ? `\n...以及 ${errors.length - 3} 个其他错误` : '';
        
        Modal.warning({
          title: `数据导入部分失败 (成功: ${success}, 失败: ${failed})`,
          content: (
            <div>
              <p>部分数据导入失败，请检查以下错误:</p>
              <pre style={{maxHeight: '200px', overflow: 'auto'}}>
                {errorMessages + moreErrors}
              </pre>
              <p>请修正错误后重新导入。</p>
            </div>
          ),
        });
      } else {
        message.success(`导入成功: ${success}条学生数据`);
      }
      
      // 重新获取学生列表
      fetchStudents();
    } catch (error) {
      console.error('导入失败:', error);
      const errorMsg = error.response?.data?.message || error.message || '未知错误';
      message.error(`导入失败: ${errorMsg}`);
      Modal.error({
        title: '数据导入失败',
        content: (
          <div>
            <p>导入学生数据时发生错误:</p>
            <p>{errorMsg}</p>
            <p>请检查您的Excel/CSV文件格式是否正确，并确保包含必填字段：学生姓名、学籍号、性别、家长姓名、家长电话。</p>
          </div>
        )
      });
      onError('导入失败');
    } finally {
      setLoadingImport(false);
    }
  };

  // 表格列定义
  const columns = [
    {
      title: '学生姓名',
      dataIndex: 'student_name',
      key: 'student_name',
    },
    {
      title: '学籍号',
      dataIndex: 'student_id',
      key: 'student_id',
    },
    {
      title: '性别',
      dataIndex: 'gender',
      key: 'gender',
    },
    {
      title: '学校',
      dataIndex: 'school_name',
      key: 'school_name',
    },
    {
      title: '班级',
      dataIndex: 'class_name',
      key: 'class_name',
      render: (text, record) => {
        return record.class_name ? (record.grade_name ? `${record.grade_name} ${record.class_name}` : record.class_name) : '-';
      }
    },
    {
      title: '家长姓名',
      dataIndex: 'parent1_name',
      key: 'parent1_name',
    },
    {
      title: '联系电话',
      dataIndex: 'parent1_phone',
      key: 'parent1_phone',
    },
    {
      title: '现居住地址',
      dataIndex: 'current_address',
      key: 'current_address',
      ellipsis: true,
    },
    {
      title: '操作',
      key: 'action',
      render: (_, record) => (
        <Space size="small">
          <Tooltip title="查看详情">
            <Button
              type="text"
              icon={<EyeOutlined />}
              onClick={() => navigate(`/students/view/${record.id}`)}
            />
          </Tooltip>
          <Tooltip title="编辑">
            <Button
              type="text"
              icon={<EditOutlined />}
              onClick={() => navigate(`/students/edit/${record.id}`)}
            />
          </Tooltip>
          <Tooltip title="删除">
            <Button
              type="text"
              danger
              icon={<DeleteOutlined />}
              onClick={() => handleDelete(record.id)}
            />
          </Tooltip>
        </Space>
      ),
    },
  ];

  return (
    <div>
      <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <h2>学生管理</h2>
        <Space>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => navigate('/students/add')}
          >
            添加学生
          </Button>
          {selectedRowKeys.length > 0 && (
            <>
              <Button
                danger
                icon={<DeleteOutlined />}
                onClick={handleBatchDelete}
              >
                批量删除 ({selectedRowKeys.length})
              </Button>
              <Button
                onClick={handleClearSelection}
              >
                取消选择
              </Button>
            </>
          )}
          <Button
            icon={<DownloadOutlined />}
            onClick={handleDownloadTemplate}
          >
            下载导入模板
          </Button>
          <Upload
            name="file"
            accept=".csv, .xlsx, .xls"
            showUploadList={false}
            customRequest={handleFileUpload}
            disabled={!selectedSchool || (currentUser?.role === 'teacher' && !selectedClass)}
            beforeUpload={(file) => {
              const isExcelOrCsv = 
                file.type === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' || 
                file.type === 'application/vnd.ms-excel' || 
                file.type === 'text/csv' ||
                /\.(xlsx|xls|csv)$/.test(file.name.toLowerCase());
              
              if (!isExcelOrCsv) {
                message.error('只能上传 Excel 或 CSV 文件!');
                return Upload.LIST_IGNORE;
              }
              
              const selectedClassRecord = classes.find((item) => normalizeId(item.id) === normalizeId(selectedClass));
              const uploadSchoolId = normalizeId(selectedClassRecord?.school_id ?? selectedClassRecord?.schoolId)
                || normalizeId(selectedSchool)
                || getUserSchoolId(currentUser);
              
              if (!uploadSchoolId) {
                message.error('请先选择学校!');
                return Upload.LIST_IGNORE;
              }
              if (currentUser?.role === 'teacher' && !selectedClass) {
                message.error('教师导入学生资料前请先选择班级!');
                return Upload.LIST_IGNORE;
              }
              
              return true;
            }}
          >
            <Button 
              icon={<UploadOutlined />} 
              loading={loadingImport}
              disabled={!selectedSchool || (currentUser?.role === 'teacher' && !selectedClass)}
              title={
                currentUser?.role === 'teacher' 
                  ? (selectedClass ? "批量导入当前班级学生数据（Excel或CSV文件）" : "请先选择班级")
                  : (!selectedSchool ? "请先选择学校" : "批量导入学生数据（Excel或CSV文件）")
              }
            >
              批量导入学生数据
              {currentUser?.role !== 'teacher' && !selectedSchool && "（请先选择学校）"}
            </Button>
          </Upload>
          <Button
            icon={<SearchOutlined />}
            onClick={fetchStudents}
            loading={loading}
            title="刷新学生列表"
          >
            刷新列表
          </Button>
          {process.env.NODE_ENV !== 'production' && (
            <>
              <Button
                type="dashed"
                onClick={() => window.open('/debug.html', '_blank')}
                title="打开调试页面"
              >
                调试页面
              </Button>
              <Button 
                type="dashed"
                onClick={() => {
                  console.log('认证状态:', {
                    isAuthenticated,
                    authLoading,
                    currentUser,
                    token: localStorage.getItem('token')
                  });
                  message.info(`已登录: ${isAuthenticated}, 用户: ${currentUser?.username}`);
                }}
                title="显示认证状态"
              >
                认证状态
              </Button>
              <Button 
                type="dashed"
                onClick={() => {
                  // 强制刷新，绕过缓存
                  localStorage.removeItem('students_cache');
                  // 刷新API连接状态
                  fetch('http://127.0.0.1:4100/api/v2/students/debug')
                    .then(res => res.json())
                    .then(data => {
                      console.log('调试API响应:', data);
                      message.success(`API连接正常，找到 ${data.count} 条学生记录`);
                      fetchStudents();
                    })
                    .catch(err => {
                      console.error('API连接测试失败:', err);
                      message.error('API连接测试失败: ' + err.message);
                    });
                }}
                title="强制刷新数据"
              >
                强制刷新
              </Button>
            </>
          )}
        </Space>
      </div>

      <Row gutter={16} style={{ marginBottom: 16 }}>
        <Col span={6}>
          <Select
            placeholder="选择学校"
            style={{ width: '100%' }}
            onChange={handleSchoolChange}
            value={selectedSchool}
            disabled={currentUser?.role === 'school_admin' && schools.length === 1}
            showSearch
            optionFilterProp="children"
            filterOption={(input, option) =>
              (option?.children ?? '').toLowerCase().includes(input.toLowerCase())
            }
          >
            {schools.map(school => (
              <Option key={school.id} value={normalizeId(school.id)}>{school.name}</Option>
            ))}
          </Select>
        </Col>
        <Col span={6}>
          <Select
            placeholder="选择班级"
            style={{ width: '100%' }}
            onChange={value => setSelectedClass(value)}
            value={selectedClass}
            disabled={!selectedSchool}
            allowClear
            showSearch
            optionFilterProp="children"
            filterOption={(input, option) =>
              (option?.children ?? '').toLowerCase().includes(input.toLowerCase())
            }
          >
            {classes.map(cls => (
              <Option key={cls.id} value={cls.id}>
                {cls.grade ? `${cls.grade} ${cls.name}` : cls.name}
              </Option>
            ))}
          </Select>
        </Col>
        <Col span={6}>
          <Input
            placeholder="按学生姓名搜索"
            prefix={<SearchOutlined />}
            onChange={e => setSearchName(e.target.value)}
            allowClear
          />
        </Col>
      </Row>

      <Table
        columns={columns}
        dataSource={students}
        rowKey="id"
        rowSelection={{
          selectedRowKeys,
          onChange: (selectedKeys) => {
            setSelectedRowKeys(selectedKeys);
          },
          selections: [
            Table.SELECTION_ALL,
            Table.SELECTION_INVERT,
            Table.SELECTION_NONE,
          ],
        }}
        loading={{
          spinning: loading,
          tip: "正在加载学生数据...",
          size: "large"
        }}
        pagination={{
          current: pagination.current,
          pageSize: pagination.pageSize,
          total: pagination.total,
          showSizeChanger: true,
          pageSizeOptions: ['10', '20', '50', '100'],
          showTotal: (total) => `共 ${total} 条记录`
        }}
        onChange={(p) => {
          fetchStudents(p.current, p.pageSize);
        }}
        locale={{ 
          emptyText: '暂无数据，请尝试重新查询或导入学生数据' 
        }}
      />
    </div>
  );
};

export default StudentList; 
